import { ArrowLeft, LucideIcon } from 'lucide-react';

interface NavigationAction {
    icon: LucideIcon;
    onClick: () => void;
    disabled?: boolean;
    className?: string;
    iconClassName?: string;
}

interface NavigationBarProps {
    title: string;
    subtitle?: string;
    onBack?: () => void;
    leftAction?: NavigationAction;
    secondaryRightAction?: NavigationAction; // Kept for backward compatibility
    rightAction?: NavigationAction | NavigationAction[]; // Support single or multiple
    rightContent?: React.ReactNode; // For custom components like LanguageSelector
    fixed?: boolean;
    className?: string;
    titleIcon?: React.ReactNode;
}

export function NavigationBar({
    title, subtitle, onBack, leftAction, secondaryRightAction, rightAction,
    rightContent, fixed = true, className = '', titleIcon
}: NavigationBarProps) {
    const renderAction = (action: NavigationAction, index?: number, isLast = false) => (
        <button
            key={index ?? 'single'}
            onClick={action.onClick}
            disabled={action.disabled}
            className={`p-2.5 flex items-center ${isLast ? '-mr-2' : ''} ${action.disabled ? 'opacity-30 cursor-not-allowed' : ''} ${action.className || 'text-blue-600 dark:text-blue-400'}`}
        >
            <action.icon size={28} className={action.iconClassName} />
        </button>
    );

    const rightActions = Array.isArray(rightAction) ? rightAction : (rightAction ? [rightAction] : []);

    return (
        <div className={`${fixed ? 'fixed top-0 left-0 right-0' : 'relative'} z-30 bg-transparent font-primary ${className}`}>
            <div className="max-w-2xl mx-auto flex items-center px-3 h-[68px]">
                <div className="flex-1 flex items-center gap-1 overflow-hidden">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="p-2.5 flex-shrink-0"
                            aria-label="Back"
                        >
                            <ArrowLeft size={28} className="text-blue-600 dark:text-blue-400" />
                        </button>
                    )}
                    {leftAction && renderAction(leftAction)}
                    {titleIcon && <div className="flex-shrink-0">{titleIcon}</div>}
                    <div className="flex flex-col overflow-hidden">
                        <h1 className="text-[22px] font-bold text-gray-900 dark:text-white truncate leading-tight">{title}</h1>
                        {subtitle && <p className="text-[13px] text-gray-500 dark:text-white truncate leading-tight">{subtitle}</p>}
                    </div>
                </div>

                <div className="flex-shrink-0 flex items-center justify-end gap-0.5">
                    {rightContent}
                    {secondaryRightAction && renderAction(secondaryRightAction)}
                    {rightActions.map((action, i) => renderAction(action, i, i === rightActions.length - 1))}
                </div>
            </div>
        </div>
    );
}
